import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Marseillan' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Marseillan',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Marseillan' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Marseillan. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous à Marseillan-Plage pour les résidences secondaires ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous intervenons à Marseillan-Plage et dans tout Marseillan. Les résidences secondaires en bord de mer subissent une forte corrosion saline. Nous utilisons des pièces anticorrosion et pouvons intervenir en votre absence après accord.',
      },
    },
    {
      '@type': 'Question',
      name: 'Les baies coulissantes des résidences de Marseillan-Plage sont-elles plus fragiles ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, le sel et le sable accélèrent l\'usure. Nous recommandons un entretien annuel des rails et roulettes. En cas de panne, nous intervenons avec des pièces renforcées anticorrosion. Diagnostic gratuit au 09 80 80 22 42.',
      },
    },
  ],
};

export default function MarseillanPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Marseillan | DFPRO"
        description="Baie coulissante, volet roulant ou vitrage en panne à Marseillan et Marseillan-Plage ? DFPRO intervient 24-48h, 7j/7. Devis gratuit, pièces anticorrosion."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-marseillan/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Marseillan"
        subtitle="Entre étang de Thau et Méditerranée : nos techniciens réparent vos baies, volets et vitrages à Marseillan et Marseillan-Plage. Devis gratuit, pièces anticorrosion."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Baie ou volet rongé par le sel à Marseillan ?"
        ctaText="Devis gratuit, pièces anticorrosion, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent à Marseillan et sur le littoral ouest, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Marseillan, entre étang de Thau et Méditerranée, expose ses menuiseries à une double corrosion saline. Baies coulissantes aux roulettes rouillées, volets bloqués par l'humidité : nous intervenons avec des pièces anticorrosion. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/marseillan-baie-01.webp"
              alt="Réparation de baie vitrée coulissante à Marseillan"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante : double corrosion étang + mer</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Le sel attaque les menuiseries de tous les côtés à Marseillan. Les roulettes de baie rouillent deux fois plus vite qu'ailleurs. Une baie qui <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince</Link> est le signe qu'il faut intervenir avant que le rail ne soit endommagé.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Roulettes anticorrosion</Link>, <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link>.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant et store : protéger du vent et du sel</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">Volets roulants</Link> : <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">blocage</Link> après le vent, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link> en surchauffe, <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link>.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-store-herault/" className="text-accent-500 font-semibold hover:underline">Stores</Link> : <Link to="/reparation-store-banne-herault/" className="text-accent-500 font-semibold hover:underline">bannes</Link> aux toiles arrachées, <Link to="/reparation-bso-herault/" className="text-accent-500 font-semibold hover:underline">BSO</Link>, <Link to="/motorisation-store-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link>.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/marseillan-volet-02.jpg"
              alt="Réparation de volet roulant à Marseillan"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/marseillan-vitrage-03.jpg"
              alt="Remplacement de vitrage à Marseillan"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier et fenêtre pour résidences secondaires</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link> : <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">vitre cassée</Link>, <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">vitrage embué</Link> (très fréquent en résidence secondaire), <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link> isolant.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Fenêtres</Link> : <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link> étanches, <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémones</Link>. <Link to="/vitrier-urgence-herault/" className="text-accent-500 font-semibold hover:underline">Urgence</Link> 7j/7.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Marseillan sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Marseillan : centre-ville, Marseillan-Plage, abords de l'étang. Communes voisines : <Link to="/depannage-menuiserie-agde/" className="text-accent-500 font-semibold hover:underline">Agde</Link>, <Link to="/depannage-menuiserie-sete/" className="text-accent-500 font-semibold hover:underline">Sète</Link>, <Link to="/depannage-menuiserie-meze/" className="text-accent-500 font-semibold hover:underline">Mèze</Link>. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}